import React, { useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormattedMessage, useIntl } from "react-intl";
import AxionControls from "../../../components/controls/AxionControls";
import { formatQuantity } from "../../cmms/cmmsFormat";
import { HideIfNoRole } from "../../../auth/authUi";
import { CMMS_TECHNICIAN_ROLES } from "../../cmms/cmmsRoles";
import {
  deleteMaterialUsage,
  getMaterialUsage,
} from "../../../fetchers/cmmsWorkOrder";
import { pushErrorMessage, pushNotification } from "../../../utils/notifications";
import MaterialUsageFormDialog from "./MaterialUsageFormDialog";
import cmmsMessages from "../../cmms/cmmsMessages";
import messages from "../messages";
import { CmmsListSkeleton, CmmsErrorState, formatCmmsError } from "../../cmms/components/CmmsStates";
import { getAssetHierarchyPalette, getContainedButtonSx } from "../../cmms/assetHierarchyStyles";

export default function MaterialUsageList({ workOrderId, companyKey, userNameById }) {
  const theme = useTheme();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const intl = useIntl();
  const queryClient = useQueryClient();
  const loadFailed = intl.formatMessage(messages.materialsLoadFailed);
  const [formState, setFormState] = useState({ open: false, material: null });
  const [confirmation, setConfirmation] = useState(null);

  const materialsQuery = useQuery(
    ["cmmsWorkOrderMaterials", companyKey, workOrderId],
    () => getMaterialUsage(companyKey, workOrderId),
    { enabled: Boolean(companyKey && workOrderId), staleTime: 30000 },
  );

  const deleteMutation = useMutation({
    mutationFn: (materialId) => deleteMaterialUsage(companyKey, workOrderId, materialId),
  });

  const materials = (materialsQuery.data ?? [])
    .slice()
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

  const totalCost = materials.reduce((sum, m) => {
    if (m.unitCost === null || m.unitCost === undefined) return sum;
    return sum + Number(m.unitCost) * Number(m.quantity ?? 0);
  }, 0);

  const handleAdd = () => setFormState({ open: true, material: null });
  const handleEdit = (material) => setFormState({ open: true, material });
  const closeForm = () => setFormState({ open: false, material: null });
  const handleSuccess = async () => {
    await materialsQuery.refetch();
  };

  const handleDelete = (material) => {
    setConfirmation({
      title: intl.formatMessage(messages.materialsDeleteTitle),
      message: intl.formatMessage(messages.materialsDeleteMessage, { description: material.description }),
      confirmText: intl.formatMessage(cmmsMessages.delete),
      onConfirm: async () => {
        try {
          await deleteMutation.mutateAsync(material.id);
          setConfirmation(null);
          // Deleting a registered row puts the parts back on the shelf, so the register's
          // balance moves here too. Free-text rows touch no balance, but telling the two apart
          // to save one invalidation would be a rule to get wrong for no gain.
          await queryClient.invalidateQueries({ queryKey: ["cmmsSpareParts"] });
          pushNotification(intl.formatMessage(messages.toastMaterialDeleted));
          await materialsQuery.refetch();
        } catch (error) {
          pushErrorMessage(formatCmmsError(error, loadFailed));
        }
      },
    });
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          <FormattedMessage {...messages.materialsCount} values={{ count: materials.length }} />
        </Typography>
        {/* The material endpoints are Cmms_Technician. Without this gate an operator sees
            and presses controls that can only 403. */}
        <HideIfNoRole anyOf={CMMS_TECHNICIAN_ROLES}>
          <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={handleAdd} sx={getContainedButtonSx(cmmsColors)}>
            <FormattedMessage {...messages.materialsAdd} />
          </Button>
        </HideIfNoRole>
      </Stack>

      {materialsQuery.isLoading &&
        <CmmsListSkeleton rows={3} height={28} />
      }
      {materialsQuery.error &&
        <CmmsErrorState error={materialsQuery.error} fallback={loadFailed} onRetry={() => materialsQuery.refetch()} isRetrying={materialsQuery.isFetching} dense />
      }
      {!materialsQuery.isLoading && !materialsQuery.error && materials.length === 0 &&
        <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic" }}>
          <FormattedMessage {...messages.materialsEmpty} />
        </Typography>
      }
      {materials.length > 0 &&
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><FormattedMessage {...messages.columnPartNumberShort} /></TableCell>
              <TableCell><FormattedMessage {...messages.fieldDescription} /></TableCell>
              <TableCell align="right"><FormattedMessage {...messages.columnQuantityShort} /></TableCell>
              <TableCell><FormattedMessage {...messages.materialsUnit} /></TableCell>
              <TableCell align="right"><FormattedMessage {...messages.materialsUnitCost} /></TableCell>
              <TableCell><FormattedMessage {...messages.columnUsedBy} /></TableCell>
              <TableCell sx={{ width: 90 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {materials.map((material) => {
              const usedBy = userNameById?.get(material.usedByUserId) ?? `#${material.usedByUserId}`;
              return (
                <TableRow key={material.id} hover>
                  {/*
                    * Only a row with a spare part id counts against stock. Without a mark, a
                    * reader has no way to tell which of two rows with the same part number
                    * moved the balance — the free-text one merely names it.
                    */}
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <span>{material.partNumber ?? "—"}</span>
                      {material.sparePartId &&
                        <Tooltip title={intl.formatMessage(messages.materialsRegisteredRow)}>
                          <Inventory2Icon fontSize="inherit" sx={{ color: "text.secondary" }} />
                        </Tooltip>
                      }
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ wordBreak: "break-word" }}>{material.description}</TableCell>
                  {/*
                    * Two decimals, not three. MaterialUsage.Quantity is decimal(10,2),
                    * so the third digit was always zero and promised a precision the column
                    * cannot hold. Matches every other quantity in the register and the ledger,
                    * and the unit cost beside it.
                    */}
                  <TableCell align="right">{formatQuantity(intl, material.quantity, 2)}</TableCell>
                  <TableCell>{material.unit}</TableCell>
                  <TableCell align="right">
                    {material.unitCost === null || material.unitCost === undefined ? "—" : formatQuantity(intl, material.unitCost, 2)}
                  </TableCell>
                  <TableCell>{usedBy}</TableCell>
                  <TableCell>
                    <HideIfNoRole anyOf={CMMS_TECHNICIAN_ROLES}>
                      <IconButton size="small" aria-label={intl.formatMessage(messages.materialsEditTitle)} onClick={() => handleEdit(material)}>
                        <EditIcon fontSize="inherit" />
                      </IconButton>
                      <IconButton size="small" aria-label={intl.formatMessage(messages.materialsDeleteTitle)} onClick={() => handleDelete(material)}>
                        <DeleteOutlineIcon fontSize="inherit" />
                      </IconButton>
                    </HideIfNoRole>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      }
      {materials.length > 0 && totalCost > 0 &&
        <Typography variant="body2" sx={{ fontWeight: 700, mt: 1, textAlign: "right" }}>
          <FormattedMessage {...messages.materialsTotalCost} values={{ amount: formatQuantity(intl, totalCost, 2) }} />
        </Typography>
      }

      <MaterialUsageFormDialog
        open={formState.open}
        material={formState.material}
        workOrderId={workOrderId}
        companyKey={companyKey}
        onClose={closeForm}
        onSuccess={handleSuccess}
      />

      <AxionControls.AxionConfirmation
        open={Boolean(confirmation)}
        title={confirmation?.title}
        message={confirmation?.message}
        confirmText={confirmation?.confirmText ?? intl.formatMessage(cmmsMessages.confirm)}
        cancelText={intl.formatMessage(cmmsMessages.cancel)}
        onConfirm={confirmation?.onConfirm}
        onCancel={() => setConfirmation(null)}
      />
      <AxionControls.AxionBackdrop open={deleteMutation.isPending || deleteMutation.isLoading} />
    </Box>
  );
}
